import { useState, useEffect } from "react";
import { getApiKeys, bedrockCostCheck } from "../api";

const fmt = (n, decimals = 2) =>
  typeof n === "number" ? n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : "—";

const fmtInt = (n) =>
  typeof n === "number" ? n.toLocaleString() : "—";

function KeyTypeTag({ apiKey }) {
  if (!apiKey) return <span className="badge badge-medium">—</span>;
  try {
    const parsed = JSON.parse(apiKey);
    const isMock = parsed.type === "bedrock-mock";
    return (
      <span className={`badge ${isMock ? "badge-medium" : "badge-approved"}`}>
        {isMock ? "Bedrock (mock)" : "Bedrock IAM"}
      </span>
    );
  } catch {
    return <span className="badge badge-low">Gov key</span>;
  }
}

function KeyValueDisplay({ apiKey }) {
  const [visible, setVisible] = useState(false);
  if (!apiKey) return <span className="text-muted">—</span>;

  let display = apiKey;
  try {
    const parsed = JSON.parse(apiKey);
    if (parsed.access_key_id) {
      display = visible
        ? `AccessKeyId: ${parsed.access_key_id}\nSecretAccessKey: ${parsed.secret_access_key}`
        : `${parsed.access_key_id.slice(0, 8)}…`;
    }
  } catch {
    display = visible ? apiKey : `${apiKey.slice(0, 14)}…`;
  }

  return (
    <span style={{ fontFamily: "monospace", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
      {display.split("\n").map((line, i) => (
        <span key={i} style={{ display: "block" }}>{line}</span>
      ))}
      <button
        onClick={() => setVisible((v) => !v)}
        className="btn btn-outline btn-sm"
        style={{ padding: "2px 7px", fontSize: 11 }}>
        {visible ? "Hide" : "Show"}
      </button>
    </span>
  );
}

function CostCheckBadge({ assignmentId }) {
  const [state, setState] = useState(null);  // null | "loading" | result
  const run = async () => {
    setState("loading");
    try {
      const r = await bedrockCostCheck(assignmentId);
      setState(r);
    } catch {
      setState({ error: true });
    }
  };
  if (state === null) return (
    <button className="btn btn-outline btn-sm" onClick={run}>AWS Check</button>
  );
  if (state === "loading") return <span className="text-muted" style={{ fontSize: 12 }}>Checking…</span>;
  if (state.error) return <span style={{ color: "var(--danger)", fontSize: 12 }}>Error</span>;
  const good = Math.abs(state.variance_pct) < 5;
  return (
    <span style={{ fontSize: 12 }}>
      <span style={{ color: good ? "var(--success)" : "var(--danger)", fontWeight: 700 }}>
        AWS: ${fmt(state.aws_cost_usd)} ({state.variance_pct > 0 ? "+" : ""}{fmt(state.variance_pct)}%)
      </span>
      <span className="text-muted" style={{ marginLeft: 4 }}>
        via {state.aws_source}
      </span>
    </span>
  );
}

export default function UserStats({ auth }) {
  const isManager = auth.role === "manager";
  const [view, setView] = useState("me");  // "me" | "team"
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let params = {};
    if (view === "me") {
      params.requester_email = auth.email;
    } else if (view === "team" && auth.managed_team) {
      params.manager_email = auth.email;
    }
    setLoading(true);
    setError("");
    getApiKeys(params)
      .then(setKeys)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [view, auth.email, auth.managed_team]);

  // Summary stats
  const totalCalls   = keys.reduce((s, k) => s + (k.total_calls || 0), 0);
  const totalTokens  = keys.reduce((s, k) => s + (k.total_tokens || 0), 0);
  const totalCost    = keys.reduce((s, k) => s + (k.estimated_cost_usd || 0), 0);
  const activeKeys   = keys.filter((k) => k.is_active).length;

  return (
    <>
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1>API Keys &amp; Usage</h1>
            <p>
              {view === "me"
                ? "Your active API keys and utilization stats."
                : "Your reportees' API keys and utilization stats."}
            </p>
          </div>
          {isManager && (
            <div style={{ display: "flex", background: "var(--border)", borderRadius: 8, padding: 3, gap: 3 }}>
              {[["me", "My Keys"], ["team", "Reportees Keys"]].map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  style={{
                    padding: "7px 16px",
                    borderRadius: 6, border: "none", cursor: "pointer",
                    fontWeight: 600, fontSize: 13, fontFamily: "inherit",
                    background: view === v ? "var(--surface)" : "transparent",
                    color: view === v ? "var(--primary)" : "var(--muted)",
                    boxShadow: view === v ? "var(--shadow)" : "none",
                    transition: ".15s",
                  }}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="page-body">
        {/* Summary tiles */}
        <div className="stat-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}>
          <div className="stat-tile">
            <div className="label">Active Keys</div>
            <div className="value" style={{ color: "var(--primary)" }}>{activeKeys}</div>
          </div>
          <div className="stat-tile">
            <div className="label">Total Keys</div>
            <div className="value">{keys.length}</div>
          </div>
          <div className="stat-tile">
            <div className="label">API Calls</div>
            <div className="value">{fmtInt(totalCalls)}</div>
          </div>
          <div className="stat-tile">
            <div className="label">Tokens Used</div>
            <div className="value">{fmtInt(totalTokens)}</div>
          </div>
          <div className="stat-tile">
            <div className="label">Est. Cost</div>
            <div className="value" style={{ color: totalCost > 0 ? "var(--danger)" : "var(--success)", fontSize: 22 }}>
              ${fmt(totalCost)}
            </div>
          </div>
        </div>

        {/* Keys table */}
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>
              {view === "me" ? "Your API Keys" : "Reportees API Keys"}
            </span>
            <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 400 }}>
              Click "AWS Check" on a Bedrock key to reconcile against Cost Explorer
            </span>
          </div>

          {loading && <div className="empty">Loading…</div>}
          {error   && <div className="empty" style={{ color: "var(--danger)" }}>{error}</div>}
          {!loading && !error && keys.length === 0 && (
            <div className="empty">
              {view === "me"
                ? "You have no assigned API keys yet."
                : "No API keys found for this team."}
            </div>
          )}
          {!loading && !error && keys.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Request</th>
                    {view === "team" && <th>Requester</th>}
                    <th>Model</th>
                    <th>Type</th>
                    <th>Key</th>
                    <th style={{ textAlign: "right" }}>Calls</th>
                    <th style={{ textAlign: "right" }}>Tokens</th>
                    <th style={{ textAlign: "right" }}>Cost (USD)</th>
                    <th>AWS Reconcile</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => {
                    const isBedrockKey = (() => {
                      try { return JSON.parse(k.api_key)?.type?.startsWith("bedrock"); } catch { return false; }
                    })();
                    return (
                      <tr key={k.id}>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{k.request_title}</div>
                          <div className="text-muted">#{k.request_id} · {k.domain}</div>
                        </td>
                        {view === "team" && (
                          <td>
                            <div style={{ fontSize: 13 }}>{k.requester}</div>
                            <div className="text-muted">{k.requester_email}</div>
                          </td>
                        )}
                        <td>
                          <div style={{ fontSize: 13 }}>{k.model_name}</div>
                          <div className="text-muted">{k.model_provider}</div>
                        </td>
                        <td><KeyTypeTag apiKey={k.api_key} /></td>
                        <td><KeyValueDisplay apiKey={k.api_key} /></td>
                        <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtInt(k.total_calls)}</td>
                        <td style={{ textAlign: "right" }}>{fmtInt(k.total_tokens)}</td>
                        <td style={{ textAlign: "right", fontWeight: 700,
                                     color: k.estimated_cost_usd > 0 ? "var(--danger)" : "inherit" }}>
                          ${fmt(k.estimated_cost_usd)}
                        </td>
                        <td>
                          {isBedrockKey
                            ? <CostCheckBadge assignmentId={k.id} />
                            : <span className="text-muted" style={{ fontSize: 12 }}>N/A</span>}
                        </td>
                        <td>
                          <span className={`badge ${k.is_active ? "badge-approved" : "badge-medium"}`}>
                            {k.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
